import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserStatus } from '../../../shared/enums/status';
import {
  formatUserStatus,
  getUserStatusBadgeClass,
  getUserStatusDotClass,
} from '../../../shared/utils/statusUtils';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUpdateUserStatusMutation } from '../../../hooks/useDashboardQueries';
import { parseApiError } from '../../../utils/parseApiError';
import { ConfirmChangeDialog } from './components/ConfirmChangeDialog';

interface UserStatusControlProps {
  userId: string;
  status: UserStatus;
}

type AdminStatus = 'active' | 'inactive' | 'blocked';

const ADMIN_STATUSES: AdminStatus[] = ['active', 'inactive', 'blocked'];

const isAdminStatus = (s: string): s is AdminStatus =>
  ADMIN_STATUSES.includes(s as AdminStatus);

/**
 * Account status: a read-only pill, or — with `users.manage` — a segmented
 * control whose selected segment *is* the current status (no separate badge
 * repeating it).
 *
 * Every change goes through an "Are you sure?" step naming old → new status,
 * so an accidental click can't lock out a real user.
 */
export const UserStatusControl = memo(function UserStatusControl({
  userId,
  status,
}: UserStatusControlProps) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManage = has('users.manage');
  const mutation = useUpdateUserStatusMutation(userId);
  const [error, setError] = useState<string | undefined>();

  const [pending, setPending] = useState<AdminStatus | null>(null);
  const label = (s: string) => t(`dashboard.filters.userStatus.${s}`);

  const handleChange = (next: AdminStatus) => {
    if (next === status) return;
    setPending(next);
  };

  const handleConfirm = async () => {
    if (!pending) return;
    const next = pending;
    setPending(null);
    setError(undefined);
    try {
      await mutation.mutateAsync(next);
    } catch (e) {
      setError(parseApiError(e, '').message);
    }
  };

  if (!canManage || !isAdminStatus(status)) {
    return (
      <span className={`status-badge ${getUserStatusBadgeClass(status)}`}>
        <span className={`status-dot ${getUserStatusDotClass(status)}`} />
        {formatUserStatus(status)}
      </span>
    );
  }

  return (
    <div className="user-status-control">
      <div
        className="ud-segmented"
        role="radiogroup"
        aria-label={t('dashboard.userDetails.statusSelectAria')}
      >
        {ADMIN_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={s === status}
            className={`ud-segment ud-segment-${s}${s === status ? ' is-selected' : ''}`}
            disabled={mutation.isPending}
            onClick={() => handleChange(s)}
          >
            {label(s)}
          </button>
        ))}
      </div>
      {error && (
        <span className="user-status-error" role="alert">
          {error}
        </span>
      )}
      <ConfirmChangeDialog
        open={pending !== null}
        message={t('dashboard.userDetails.confirm.statusMessage', {
          from: label(status),
          to: pending ? label(pending) : '',
        })}
        note={
          pending === 'blocked'
            ? t('dashboard.userDetails.confirmBlock')
            : pending === 'inactive'
              ? t('dashboard.userDetails.confirm.inactiveNote')
              : undefined
        }
        danger={pending === 'blocked'}
        onCancel={() => setPending(null)}
        onConfirm={() => void handleConfirm()}
      />
    </div>
  );
});

export default UserStatusControl;
